import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { fetchHats } from "@/lib/api";

const SPANS = [
  "lg:col-span-7",
  "lg:col-span-5",
  "lg:col-span-4",
  "lg:col-span-4",
  "lg:col-span-4",
  "lg:col-span-5",
  "lg:col-span-7",
  "lg:col-span-6 lg:col-start-4",
];

export default function Gallery() {
  const [hats, setHats] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchHats().then(setHats).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-testid="gallery-page" className="pt-20">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24">
        <Reveal><Eyebrow className="mb-6">Est. United Kingdom — Our Hats</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight" data-testid="gallery-headline">
            Our <span className="italic text-green">Hats</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2} className="mt-8 max-w-xl">
          <p className="text-smoke leading-relaxed" data-testid="gallery-intro">
            Explore our exquisite collection of high-quality hats crafted with
            precision and style. Select any piece to read its craftsmanship
            notes and materials.
          </p>
        </Reveal>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-8" data-testid="gallery-grid">
          {hats.map((hat, i) => (
            <Reveal key={hat.slug} delay={(i % 3) * 0.08} className={SPANS[i % SPANS.length]}>
              <button
                data-testid={`gallery-item-${hat.slug}`}
                onClick={() => setActive(hat)}
                className="group block w-full text-left"
              >
                <div className="overflow-hidden bg-cream border border-line">
                  <img
                    src={hat.image}
                    alt={hat.name}
                    loading="lazy"
                    className="w-full aspect-square object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  />
                </div>
                <div className="flex items-baseline justify-between mt-5">
                  <div>
                    <h2 className="font-serif text-xl md:text-2xl tracking-tight group-hover:italic transition-all duration-500">
                      {hat.name}
                    </h2>
                    <p className="eyebrow !text-[0.6rem] text-smoke mt-2">{hat.materials}</p>
                  </div>
                  <span className="eyebrow !text-[0.6rem] text-honey opacity-0 group-hover:opacity-100 transition-opacity duration-500 shrink-0 ml-4">
                    View →
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24 border border-line bg-cream p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Eyebrow className="mb-4">Bespoke</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="custom-designs-note">
              Looking for something one-of-a-kind?
            </h2>
            <p className="mt-4 text-smoke leading-relaxed max-w-lg">
              Custom hat designs are available on request. Tell us the cloth,
              the cut and the occasion — we'll make it properly.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="custom-designs-cta"
              className="group inline-flex items-center gap-3 bg-green text-ivory eyebrow !text-[0.625rem] px-8 py-5 hover:bg-ink transition-colors duration-300"
            >
              Start a commission
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </Reveal>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="hat-detail-modal"
            className="fixed inset-0 z-[70] flex items-end md:items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="absolute inset-0 bg-ink/45 backdrop-blur-sm" onClick={() => setActive(null)} />
            <motion.div
              className="relative bg-ivory w-full md:max-w-5xl border border-line grid md:grid-cols-2 max-h-[92vh] overflow-y-auto"
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                data-testid="hat-detail-close"
                onClick={() => setActive(null)}
                aria-label="Close detail"
                className="absolute top-5 right-5 p-2 text-smoke hover:text-ink transition-colors duration-300 z-10 bg-ivory/80"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
              <div className="bg-cream border-b md:border-b-0 md:border-r border-line">
                <img
                  src={active.image}
                  alt={active.name}
                  data-testid="hat-detail-image"
                  className="w-full h-full object-cover aspect-square"
                />
              </div>
              <div className="p-10 md:p-14 flex flex-col justify-center">
                <Eyebrow className="mb-5">The Collection — {active.materials.split(",")[0]}</Eyebrow>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="hat-detail-name">
                  {active.name}
                </h2>
                <div className="mt-10 space-y-8">
                  <div>
                    <p className="eyebrow !text-[0.6rem] text-smoke mb-2">Materials</p>
                    <p className="text-sm leading-relaxed" data-testid="hat-detail-materials">{active.materials}</p>
                  </div>
                  <div>
                    <p className="eyebrow !text-[0.6rem] text-smoke mb-2">Craftsmanship notes</p>
                    <p className="font-serif italic text-lg leading-relaxed text-smoke" data-testid="hat-detail-note">
                      “{active.note}”
                    </p>
                  </div>
                  <div className="pt-4 border-t border-line">
                    <p className="text-xs text-smoke leading-relaxed">
                      Custom hat designs are available on request.{" "}
                      <Link
                        to="/contact"
                        data-testid="hat-detail-enquire"
                        className="underline underline-offset-4 decoration-honey hover:text-honey transition-colors duration-300"
                        onClick={() => setActive(null)}
                      >
                        Enquire with our team
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
